import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Mark, mergeAttributes } from '@tiptap/core';
import { useAuth } from '../../contexts/AuthContext';
import '../KnowledgeArticleEditorPage/KnowledgeArticleEditorPage.css';

const FontSize = Mark.create({
    name: 'fontSize',
    addAttributes() {
        return {
            size: {
                default: null,
                parseHTML: (element) => element.style.fontSize || null,
                renderHTML: (attributes) => {
                    if (!attributes.size) return {};
                    return { style: `font-size: ${attributes.size}` };
                }
            }
        };
    },
    parseHTML() {
        return [{ tag: 'span[style*=font-size]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes), 0];
    },
    addCommands() {
        return {
            setFontSize: (size) => ({ chain }) => chain().setMark(this.name, { size }).run(),
            unsetFontSize: () => ({ chain }) => chain().unsetMark(this.name).run(),
        };
    }
});

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
    <button
        type="button"
        className={`tiptap-btn ${active ? 'tiptap-btn-active' : ''}`}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        {children}
    </button>
);

const EditorToolbar = ({ editor, onImageUpload, uploading }) => {
    if (!editor) return null;
    const fontSize = editor.getAttributes('fontSize')?.size || '20px';

    const setLink = () => {
        const url = window.prompt('Введите URL ссылки:');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkToLink({ href: '' }).unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
    };

    return (
        <div className="tiptap-toolbar">
            <div className="tiptap-toolbar-group">
                <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Жирный"><b>B</b></ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Курсив"><i>I</i></ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Подчёркнутый"><u>U</u></ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Зачёркнутый"><s>S</s></ToolbarButton>
            </div>
            <div className="tiptap-toolbar-group">
                <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="H2">H2</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="H3">H3</ToolbarButton>
            </div>
            <div className="tiptap-toolbar-group">
                <select
                    className="tiptap-font-size-select"
                    value={fontSize}
                    onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
                    title="Размер шрифта"
                >
                    <option value="20px">20px (рекомендованно)</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                    <option value="24px">24px</option>
                    <option value="28px">28px</option>
                </select>
            </div>
            <div className="tiptap-toolbar-group">
                <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Список">• —</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Нумерованный">1. —</ToolbarButton>
            </div>
            <div className="tiptap-toolbar-group">
                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Слева">⬅</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Центр">⬛</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Справа">➡</ToolbarButton>
            </div>
            <div className="tiptap-toolbar-group">
                <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Цитата">❝</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Код">{'</>'}</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Разделитель">—</ToolbarButton>
                <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Ссылка">🔗</ToolbarButton>
            </div>
            <div className="tiptap-toolbar-group">
                <label className={`tiptap-btn tiptap-image-btn ${uploading ? 'disabled' : ''}`} title="Вставить изображение">
                    <ImageIcon size={15} />
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onImageUpload} disabled={uploading} />
                </label>
            </div>
            <div className="tiptap-toolbar-group">
                <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Отменить">↩</ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Повторить">↪</ToolbarButton>
            </div>
        </div>
    );
};

const ArticleEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isNew = !id;

    const [title, setTitle] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [isPublished, setIsPublished] = useState(false);
    const [authorIds, setAuthorIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            FontSize,
            Image.configure({ inline: false, allowBase64: true }),
            Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder: 'Начните писать статью...' }),
        ],
        content: '',
        editorProps: {
            attributes: { class: 'tiptap-content' }
        }
    });

    const { data: article, isLoading } = useQuery(
        ['articleEdit', id],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/articles/manage/${id}`, { withCredentials: true });
            return res.data;
        },
        { enabled: !isNew, retry: false }
    );

    const { data: authorCandidates = [] } = useQuery(
        'articleAuthorCandidates',
        async () => {
            const res = await axios.get(`${API_BASE_URL}/articles/authors`, { withCredentials: true });
            return res.data;
        }
    );

    useEffect(() => {
        if (article && editor) {
            setTitle(article.title || '');
            setExcerpt(article.excerpt || '');
            setIsPublished(Boolean(article.is_published));
            setAuthorIds(
                Array.isArray(article.author_ids)
                    ? [...new Set(article.author_ids.map((v) => parseInt(v, 10)).filter((v) => Number.isInteger(v) && v > 0))]
                    : (article.author_id ? [Number(article.author_id)] : [])
            );
            editor.commands.setContent(article.content || '');
        }
    }, [article, editor]);

    useEffect(() => {
        if (!isNew || !user?.id) return;
        setAuthorIds((prev) => (prev.length > 0 ? prev : [Number(user.id)]));
    }, [isNew, user?.id]);

    useEffect(() => {
        if (!editor || !isNew) return;
        editor.chain().focus().setFontSize('20px').run();
    }, [editor, isNew]);

    const handleSave = useCallback(async () => {
        if (!title.trim()) {
            toast.error('Введите заголовок');
            return;
        }
        setSaving(true);
        try {
            const data = {
                title: title.trim(),
                content: editor?.getHTML() || '',
                excerpt: excerpt.trim() || null,
                is_published: isPublished,
                author_ids: authorIds
            };
            let result;
            if (isNew) {
                const res = await axios.post(`${API_BASE_URL}/articles`, data, { withCredentials: true });
                result = res.data;
                toast.success('Статья создана');
            } else {
                const res = await axios.put(`${API_BASE_URL}/articles/${id}`, data, { withCredentials: true });
                result = res.data;
                toast.success('Статья сохранена');
            }
            queryClient.invalidateQueries('manageableArticles');
            queryClient.invalidateQueries('articlesSearch');
            queryClient.invalidateQueries(['articleEdit', id]);
            queryClient.invalidateQueries(['article', String(result.id)]);
            if (isNew) navigate(`/articles/editor/edit/${result.id}`, { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    }, [title, excerpt, isPublished, authorIds, editor, isNew, id, navigate, queryClient]);

    const handleImageUpload = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await axios.post(`${API_BASE_URL}/articles/upload-image`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            editor?.chain().focus().setImage({ src: res.data.url }).run();
        } catch (err) {
            toast.error('Ошибка загрузки изображения');
        } finally {
            setUploading(false);
        }
    }, [editor]);

    if (!isNew && isLoading) {
        return <div className="kae-page"><div className="kae-loading"><div className="spinner" /></div></div>;
    }

    return (
        <div className="kae-page">
            <div className="kae-toolbar">
                <button className="kae-back-btn" onClick={() => navigate('/articles/editor')}>
                    <ArrowLeft size={18} />
                    К списку
                </button>
                <div className="kae-toolbar-right">
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', marginRight: 8 }}>
                        <input
                            type="checkbox"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                        />
                        Опубликована
                    </label>
                    <span className={`ke-status ${isPublished ? 'status-published' : 'status-draft'}`}>
                        {isPublished ? 'Опубликована' : 'Черновик'}
                    </span>
                    <button className="kae-save-btn" onClick={handleSave} disabled={saving}>
                        <Save size={16} />
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>

            <div className="kae-main">
                <div className="kae-editor-section">
                    <input
                        type="text"
                        className="kae-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Заголовок статьи"
                    />
                    <textarea
                        className="kae-title-input"
                        value={excerpt}
                        onChange={(e) => setExcerpt(e.target.value)}
                        placeholder="Краткое описание (для списка и карточек)"
                        rows={2}
                        style={{ fontSize: 16, fontWeight: 400, minHeight: 64, resize: 'vertical', marginBottom: 12 }}
                    />
                    <div className="kae-editors-panel">
                        <div className="kae-editors-title">Авторы статьи</div>
                        <div className="kae-editors-list">
                            {authorCandidates.map((u) => (
                                <label key={u.id} className="kae-editor-option">
                                    <input
                                        type="checkbox"
                                        checked={authorIds.includes(Number(u.id))}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const uid = Number(u.id);
                                            setAuthorIds((prev) => (
                                                checked
                                                    ? [...new Set([...prev, uid])]
                                                    : prev.filter((idVal) => idVal !== uid)
                                            ));
                                        }}
                                    />
                                    <span>{u.name}{u.email ? ` (${u.email})` : ''}</span>
                                </label>
                            ))}
                        </div>
                        {authorCandidates.length === 0 && (
                            <div style={{ fontSize: 13, color: '#6b7280' }}>Нет доступных авторов</div>
                        )}
                    </div>
                    <div className="kae-editor-wrap">
                        <EditorToolbar editor={editor} onImageUpload={handleImageUpload} uploading={uploading} />
                        <EditorContent editor={editor} className="kae-editor" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArticleEditorPage;
